import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip, // ✅ NEW
} from 'chart.js';
import { filterReviewsByPlatform } from '../../utility/reviewByPlatforms'; 
import './reviewGraph.css';

// Register necessary chart components
ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Title, Tooltip); // ✅ add Tooltip

const platformColors = {
  GYG: '#F1866A',
  HEA: '#844BF6',
  Tiqets: '#6AA84F',
  Viator: '#2A6B6D',
  Civitatis: '#E91E63',
  Airbnb: '#D3D3D3',
};

const ReviewGraph = ({ reviewData = [], title = "Reviews Average", chartId = "reviewGraphChart" }) => {
  const currentYear = new Date().getFullYear();
  const [selectedProductCode, setSelectedProductCode] = useState('All');
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());

  const months = [
    "Jan'", "Feb'", "Mar'", "Apr'", "May'", "Jun'",
    "Jul'", "Aug'", "Sept'", "Oct'", "Nov'", "Dec'"
  ];

  const { gygReviews, viatorReviews, headoutReviews, tiqetsReviews, civitatisReviews, musemetReviews, airbnbReviews } = filterReviewsByPlatform(reviewData);

  const platformData = {
    GYG: gygReviews,
    Viator: viatorReviews,
    HEA: headoutReviews,
    Tiqets: tiqetsReviews,
    Civitatis: civitatisReviews,
    Musment: musemetReviews,
    Airbnb: airbnbReviews,
  };

  const groupedData = {};
  const availableYears = new Set();

  Object.keys(platformData).forEach(platform => {
    platformData[platform].forEach(review => {
      const [day, month, year] = review[3].split("/").map(Number);
      availableYears.add(year.toString());
      const monthYear = `${months[month - 1]} ${String(year).slice(-2)}`;
      const productCode = review[6]?.trim?.() ?? '';

      if (!groupedData[platform]) groupedData[platform] = {};
      if (!groupedData[platform][productCode]) groupedData[platform][productCode] = {};
      if (!groupedData[platform][productCode][monthYear]) groupedData[platform][productCode][monthYear] = [];

      groupedData[platform][productCode][monthYear].push(review);
    });
  });

  const sortedYears = Array.from(availableYears).sort();
  const labels = months.map((m) => `${m} ${selectedYear.slice(-2)}`);

  const datasets = Object.keys(platformData).map(platform => {
    const filteredPlatformReviews = platformData[platform].filter(review => {
      const [ , , year] = review[3].split("/").map(Number);
      return year.toString() === selectedYear;
    });

    const grouped = {};
    filteredPlatformReviews.forEach(review => {
      const [ , month, year] = review[3].split("/").map(Number);
      const monthYear = `${months[month - 1]} ${String(year).slice(-2)}`;
      const productCode = review[6];

      if (selectedProductCode === 'All' || selectedProductCode === productCode) {
        if (!grouped[monthYear]) grouped[monthYear] = [];
        grouped[monthYear].push(review);
      }
    });

    const counts = labels.map(m => (grouped[m]?.length || 0));
    const averageRatings = labels.map(month => {
      const reviewsForMonth = grouped[month] || [];
      const total = reviewsForMonth.reduce((sum, r) => sum + r[4], 0);
      return reviewsForMonth.length ? total / reviewsForMonth.length : null;
    });

    return {
      label: platform,
      data: averageRatings,
      _counts: counts,                                                      // ✅ stash counts for tooltip
      fill: false,
      backgroundColor: platformColors[platform],
      borderColor: platformColors[platform],
      borderWidth: 2,
      tension: 0.3,
    };
  });

  const data = { labels, datasets };

  const options = {
    responsive: true,
    plugins: {
      title: { display: true, text: title },
      legend: { display: false },
      tooltip: {
        callbacks: {
          // Show "Platform: 4.62 (13 reviews)"
          label: function (context) {
            const ds = context.dataset;
            const idx = context.dataIndex;
            const count = ds._counts?.[idx] ?? 0;
            const y = context.parsed.y;
            if (y == null) return null; // no point
            const avg = typeof y === 'number' ? y.toFixed(3) : y;
            const plural = count === 1 ? 'review' : 'reviews';
            return `${ds.label}: ${avg} (${count} ${plural})`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: 'Month' },
        grid: { color: 'rgba(211, 211, 211, 0.35)' },
      },
      y: {
        title: { display: true, text: 'Average Rating' },
        min: 3.0,
        max: 5.1,
        ticks: {
          stepSize: 0.2,
          callback: function (value) {
            return value > 5 ? null : value;
          }
        },
        grid: { color: 'rgba(211, 211, 211, 0.35)' },
      }
    },
    spanGaps: false,
  };

  const renderDropdown = () => (
    <div className='graphDropdowns'>
      <div className='dropdownItem'>
        <label htmlFor="yearSelect">Year: </label>
        <select
          id="yearSelect"
          value={selectedYear}
          onChange={e => setSelectedYear(e.target.value)}
        >
          {sortedYears.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      <div className='dropdownItem'>
        <label htmlFor="productCode">Product Code: </label>
        <select
          id="productCode"
          value={selectedProductCode}
          onChange={e => setSelectedProductCode(e.target.value)}
        >
          <option value="All">All</option>
          {Object.keys(groupedData.GYG || {}).map(productCode => (
            <option key={productCode} value={productCode}>
              {productCode}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderLegend = () => (
    <div style={{ marginTop: '20px' }}>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {Object.keys(platformColors).map((platform) => (
          <li key={platform} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              backgroundColor: platformColors[platform],
              borderRadius: '50%',
              marginRight: '10px',
            }}></div>
            <span>{platform}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className='graphComponent'>
      <div className='graphActual'>
        <div className='graphHeading'>
          <div>{title}</div>
          {renderDropdown()}
        </div>
        <Line id={chartId} data={data} options={options} />
        <div className='legendContainer'>{renderLegend()}</div>
      </div>
    </div>
  );
};

export default ReviewGraph;
